import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AxiosError } from "axios";
import Button from "@aleaplay/web-components/ui/Button";
import Input from "@aleaplay/web-components/ui/Input";
import Modal from "@aleaplay/web-components/ui/Modal";
import Select from "@aleaplay/web-components/ui/Select";

import { Flex, FlexContainerProps } from "@/v1/components/atoms/Flex/Flex";
import { Form } from "@/v1/components/atoms/Form/Form";
import { FormChild } from "@/v1/components/atoms/FormChild";
import { TreeSelect } from "@/v1/components/molecules/TreeSelect/TreeSelect";
import { DropFilesField } from "@/v1/components/organisms/DropFilesField/DropFilesField";
import { ErrorResponse } from "@/v1/types/global";
import { catchAsync } from "@/v1/utils/errorHandler";
import { QuerySelect } from "@/v2/components/organisms/QuerySelect/QuerySelect";

import { FormProvider } from "../FormContext";

interface FormModalProps {
  children: ReactNode;
}

export enum FormModalItemType {
  INPUT = "input",
  MESSAGE = "message",
  SELECT = "select",
  UPLOAD = "upload",
  TREE = "tree",
  QUERY_SELECT = "query-select",
  CUSTOM = "custom",
}

export type FormModalItem = {
  type: FormModalItemType;
  props: any;
  flexProps?: Omit<FlexContainerProps, "children">;
  halfWidth?: boolean;
  component?: any;
};

export interface FormModalData {
  title: string;
  items: Array<FormModalItem>;
  footerMessage?: string | JSX.Element;
  showErrorOnField?: string;
  onConfirmMessage?: string;
  onConfirm?: (formData?: any) => Promise<any>;
  noValidate?: boolean;
  hideConfirmButton?: boolean;
}

interface FormModalContextProps {
  setModal: (data: FormModalData) => Promise<unknown>;
}

export const FormModalContext = createContext({} as FormModalContextProps);

// eslint-disable-next-line max-lines-per-function
export const FormModalProvider = ({ children }: FormModalProps): ReactElement => {
  const intl = useIntl();
  const FORM_ID = "form-id";
  const [data, setData] = useState<FormModalData | false>(false);
  const [loading, setLoading] = useState(false);
  const resolver = useRef<any>(null);
  const modalItems = {
    input: Input,
    message: FormattedMessage,
    select: Select,
    upload: DropFilesField,
    tree: TreeSelect,
    "query-select": QuerySelect,
  };

  const setModal = (modalData: FormModalData) => {
    setLoading(false);
    setData(modalData);

    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  };

  const handleCancel = () => {
    if (loading) return;
    resolver.current && resolver.current(false);
    setData(false);
  };

  const handleFormSuccess = catchAsync(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    async (formData: any, formMethods) => {
      if (data && data.onConfirm) {
        setLoading(true);
        if (data.showErrorOnField) {
          try {
            await data.onConfirm(formData);
            setLoading(false);
            resolver.current && resolver.current();
            setData(false);
          } catch (error) {
            const errorResponse = (error &&
              typeof error === "object" &&
              (error as AxiosError<ErrorResponse>)?.response?.data) as ErrorResponse;
            const errorMessage = errorResponse?.errors
              ? errorResponse.errors
                .map(({ code, message }) => {
                  const translationKey = [code, message].find(
                    (value) => value && intl.messages[value],
                  );
                  return translationKey ? intl.formatMessage({ id: translationKey }) : message;
                })
                .join(", ")
              : "";
            formMethods.setError(data.showErrorOnField, errorMessage);
            setLoading(false);
          }
        } else {
          const successData = await data.onConfirm(formData);
          if (successData) return successData;
        }
      }

      return formData;
    },
    {
      onEnd: (returnData) => {
        if (data && !data.showErrorOnField) {
          setLoading(false);
          const onlyConfirmModal = data.items.every((item) => item.type === "message");
          resolver.current && resolver.current(onlyConfirmModal ? true : returnData);
          setData(false);
        }
      },
    },
  );

  const contextValue = useMemo(() => ({ setModal }), []);

  return (
    <>
      <Modal
        title={data && <FormattedMessage id={data.title} />}
        domNode={document.querySelector("#root")}
        actions={[
          <Button key="cancel" modifier="secondary" ripple onClick={handleCancel}>
            <FormattedMessage id="label.cancel" />
          </Button>,
          data && !data.hideConfirmButton && (
            <Button type="submit" form={FORM_ID} key="confirm" modifier="primary" ripple>
              <FormattedMessage id={(data && data.onConfirmMessage) || "label.confirm"} />
            </Button>
          ),
        ].filter(Boolean)}
        footerMessage={data && data.footerMessage}
        visible={Boolean(data)}
        loading={loading}
        onClose={handleCancel}
      >
        <FormProvider>
          <Form
            id={FORM_ID}
            onSuccess={handleFormSuccess}
            noValidate={data && data.noValidate !== undefined ? data.noValidate : true}
          >
            <Flex container flexWrap="wrap" gap={2}>
              {data &&
                data.items.map((item) => {
                  const ModalItem =
                    item.type === FormModalItemType.CUSTOM ? item.component : modalItems[item.type];
                  const baseFlexProps = {
                    flexBasis: item.halfWidth ? "calc(50% - 4px)" : "100%",
                  };

                  if (item.type === "message") {
                    return (
                      <Flex key={item.props.id} {...baseFlexProps} {...(item.flexProps || {})}>
                        <ModalItem {...item.props} />
                      </Flex>
                    );
                  } else {
                    return (
                      <Flex key={item.props.name} {...baseFlexProps} {...(item.flexProps || {})}>
                        <FormChild>
                          {item.type === "select" ? (
                            <ModalItem {...item.props}>
                              {item.props.options.map((option: any) => (
                                <Select.Option key={option.value} {...option}>
                                  {option.label}
                                </Select.Option>
                              ))}
                            </ModalItem>
                          ) : (
                            <ModalItem {...item.props} />
                          )}
                        </FormChild>
                      </Flex>
                    );
                  }
                })}
            </Flex>
          </Form>
        </FormProvider>
      </Modal>
      <FormModalContext.Provider value={contextValue}>{children}</FormModalContext.Provider>
    </>
  );
};

export const useFormModalContext = () => useContext(FormModalContext);
